/**
 * La sesión · `BFF_BO.md` §3.3 y §3.4
 *
 * Dos piezas y viven en sitios distintos a propósito:
 *
 * | Pieza             | Dura              | Dónde                        |
 * | ----------------- | ----------------- | ---------------------------- |
 * | Token de acceso   | 15 min            | **En memoria**, aquí         |
 * | Token de refresco | 30 días rotatorio | Cookie `HttpOnly`, solo el navegador |
 *
 * **Ninguno de los dos va a `localStorage`.** Un token de refresco de 30 días en
 * `localStorage` convierte un XSS cualquiera en un mes de acceso a las fichas
 * médicas de un club.
 *
 * Consecuencia práctica, y es la que sorprende: **al recargar la página no hay
 * token de acceso**. El arranque de la aplicación es siempre
 * `POST /auth/refresh` → `GET /me`.
 */

/** En memoria y en un módulo, no en el estado de React: lo lee el cliente HTTP. */
let token: string | null = null;

/**
 * La renovación en vuelo, si hay una.
 *
 * Es el corazón de la regla 1 de §3.4: si diez peticiones dan 401 a la vez, la
 * primera refresca y las otras nueve **esperan a esta promesa**. Diez refrescos
 * en paralelo rotarían el token diez veces y ocho se quedarían con uno inválido.
 */
let enVuelo: Promise<boolean> | null = null;

type AlCerrarSesion = () => void;
let alCerrarSesion: AlCerrarSesion = () => {};

export const tokenDeAcceso = (): string | null => token;

export const fijarToken = (nuevo: string | null): void => {
  token = nuevo;
};

/** Lo llama el contexto de sesión para enterarse de que hay que volver a acceder. */
export const alPerderLaSesion = (fn: AlCerrarSesion): void => {
  alCerrarSesion = fn;
};

export const limpiarSesion = (): void => {
  token = null;
  enVuelo = null;
  alCerrarSesion();
};

/**
 * Las rutas que **nunca** entran en el bucle de refresco · regla 3 de §3.4.
 *
 * Un 401 en `/auth/refresh` significa «vuelve a entrar», no «refresca». Sin esto,
 * un refresco caducado se reintenta a sí mismo para siempre.
 */
const FUERA_DEL_BUCLE = [
  '/auth/refresh',
  '/auth/magic-link',
  '/auth/password',
  '/auth/logout',
];

export const entraEnElBucle = (url: string): boolean =>
  !FUERA_DEL_BUCLE.some((ruta) => url.includes(ruta));

/**
 * Lo que este módulo necesita de `fetch`, y nada más.
 *
 * No es `typeof globalThis.fetch` a propósito: los tipos de Bun le cuelgan un
 * `preconnect`, así que cualquier función nuestra deja de encajar. Lo que hace
 * falta aquí es «dame una petición y devuélveme una respuesta», y acotarlo así
 * además hace que el doble de las pruebas sea una función de dos líneas.
 */
export type Peticion = (
  entrada: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

export interface OpcionesDeSesion {
  /** Base de la API, sin barra final. */
  readonly base: string;
  /** Inyectable para las pruebas; por defecto el `fetch` del navegador. */
  readonly fetch?: Peticion;
}

/**
 * Renueva la sesión. Como mucho una vez a la vez.
 *
 * Devuelve `true` si hay token nuevo. En `false` la sesión está muerta y quien
 * llama tiene que dejar de insistir.
 */
export function refrescar(opciones: OpcionesDeSesion): Promise<boolean> {
  if (enVuelo) return enVuelo;

  const peticion = opciones.fetch ?? globalThis.fetch;

  enVuelo = (async () => {
    try {
      const r = await peticion(`${opciones.base}/v1/auth/refresh`, {
        method: 'POST',
        // La cookie de refresco la manda el navegador solo, y solo con esto.
        credentials: 'include',
      });

      if (!r.ok) {
        limpiarSesion();
        return false;
      }

      const cuerpo = (await r.json()) as { access_token?: string };
      if (!cuerpo.access_token) {
        limpiarSesion();
        return false;
      }

      token = cuerpo.access_token;
      return true;
    } catch {
      // Un fallo de red no es una sesión caducada, pero tampoco hay token nuevo:
      // quien llama decide si reintentar. No se limpia la sesión, que sería
      // echar a alguien de la aplicación porque se le fue el wifi un segundo.
      return false;
    } finally {
      // Se suelta siempre, también al fallar: dejarla puesta congelaría todos
      // los refrescos posteriores contra un resultado viejo.
      enVuelo = null;
    }
  })();

  return enVuelo;
}

/**
 * `fetch` con el bucle de §3.4 puesto.
 *
 * ```
 * petición → 401 ──▶ POST /auth/refresh ──┬─ 200 → reintentar la original UNA vez
 *                                         └─ 401 → limpiar sesión → pantalla de acceso
 * ```
 *
 * **Un solo reintento** (regla 2). Si el reintento vuelve a dar 401, se acabó: un
 * bucle de reintentos contra un token muerto es un bucle infinito.
 */
export function fetchConSesion(opciones: OpcionesDeSesion): Peticion {
  const peticion = opciones.fetch ?? globalThis.fetch;

  return async (entrada, init) => {
    const url =
      typeof entrada === 'string'
        ? entrada
        : entrada instanceof URL
          ? entrada.toString()
          : entrada.url;

    const conCabeceras = (): RequestInit => {
      const cabeceras = new Headers(init?.headers);
      if (token) cabeceras.set('authorization', `Bearer ${token}`);
      return { ...init, headers: cabeceras, credentials: 'include' };
    };

    const primera = await peticion(entrada, conCabeceras());

    if (primera.status !== 401 || !entraEnElBucle(url)) return primera;

    const renovada = await refrescar(opciones);
    if (!renovada) {
      limpiarSesion();
      return primera;
    }

    // El reintento, y solo uno. Las cabeceras se recalculan: el token es otro.
    return peticion(entrada, conCabeceras());
  };
}
